import React from 'react';
import { ArrowPathIcon, CurrencyDollarIcon, ClockIcon } from '@heroicons/react/24/outline';

const RefundReturns: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <ArrowPathIcon className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Refund & Returns Policy</h1>
          <p className="text-xl text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">
          
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <CurrencyDollarIcon className="h-6 w-6 text-green-600 mr-2" />
              1. Refund Policy Overview
            </h2>
            <p className="text-gray-700 leading-relaxed">
              At Rightoz, we are committed to providing fresh, high-quality milk and dairy products. 
              If you are not satisfied with our products or services, we offer refunds and returns 
              under the conditions outlined in this policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Eligible Items for Return/Refund</h2>
            <div className="text-gray-700 leading-relaxed space-y-3">
              <p>We accept returns and provide refunds for:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Products delivered in damaged or spoiled condition</li>
                <li>Incorrect products delivered (wrong type or quantity)</li>
                <li>Products that do not meet our quality standards</li>
                <li>Late deliveries beyond the committed time slot</li>
                <li>Products with unusual taste, smell, or appearance</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <ClockIcon className="h-6 w-6 text-green-600 mr-2" />
              3. Time Limits for Returns
            </h2>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="space-y-3 text-gray-700">
                <p><strong>Fresh Milk:</strong> Must be reported within 2 hours of delivery</p>
                <p><strong>Dairy Products:</strong> Must be reported within 4 hours of delivery</p>
                <p><strong>Quality Issues:</strong> Should be reported immediately upon discovery</p>
                <p><strong>Missed Deliveries:</strong> Contact us within 1 hour of scheduled delivery time</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. How to Request a Return/Refund</h2>
            <div className="text-gray-700 leading-relaxed space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                  <h3 className="font-semibold text-green-800 mb-3">📱 Via Mobile App</h3>
                  <ol className="list-decimal list-inside space-y-2 text-sm">
                    <li>Open Rightoz app</li>
                    <li>Go to "Order History"</li>
                    <li>Select the problematic order</li>
                    <li>Click "Report Issue"</li>
                    <li>Follow the instructions</li>
                  </ol>
                </div>
                <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                  <h3 className="font-semibold text-blue-800 mb-3">📞 Via Phone/WhatsApp</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Phone:</strong> +91 91144 91158</p>
                    <p><strong>WhatsApp:</strong> +91 91144 91158</p>
                    <p><strong>Email:</strong> support@rightoz.com</p>
                    <p className="text-xs mt-2">Available: 6 AM - 10 PM</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Information Required for Returns</h2>
            <div className="text-gray-700 leading-relaxed space-y-3">
              <p>When reporting an issue, please provide:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Order number and delivery date</li>
                <li>Description of the issue (damage, quality, incorrect item)</li>
                <li>Photos of the product (if applicable)</li>
                <li>Your contact information</li>
                <li>Preferred resolution (refund, replacement, credit)</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Refund Process & Timeline</h2>
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-800 mb-3">Processing Steps:</h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <span className="text-white text-xs font-bold">1</span>
                    </div>
                    <div>
                      <p className="font-medium">Issue Reported</p>
                      <p className="text-sm text-gray-600">You contact us with the issue details</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <span className="text-white text-xs font-bold">2</span>
                    </div>
                    <div>
                      <p className="font-medium">Investigation (1-2 hours)</p>
                      <p className="text-sm text-gray-600">We review your case and verify the issue</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-0.5">
                      <span className="text-white text-xs font-bold">3</span>
                    </div>
                    <div>
                      <p className="font-medium">Resolution (Same Day)</p>
                      <p className="text-sm text-gray-600">Refund processed or replacement scheduled</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Refund Methods</h2>
            <div className="text-gray-700 leading-relaxed space-y-3">
              <p>Refunds will be processed using the original payment method:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Cash on Delivery:</strong> Cash refund during next delivery or account credit</li>
                <li><strong>Digital Payments:</strong> Refund to original payment method (2-5 business days)</li>
                <li><strong>Wallet/UPI:</strong> Instant refund to original source</li>
                <li><strong>Credit/Debit Card:</strong> 3-7 business days depending on bank</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Replacement Policy</h2>
            <div className="text-gray-700 leading-relaxed space-y-3">
              <p><strong>Free Replacement:</strong> Available for quality issues or incorrect deliveries</p>
              <p><strong>Next Delivery:</strong> Replacement items can be included in your next scheduled delivery</p>
              <p><strong>Emergency Replacement:</strong> Same-day replacement for critical quality issues (subject to availability)</p>
              <p><strong>No Additional Charges:</strong> Replacements due to our error are provided free of cost</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Non-Refundable Situations</h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <p className="text-red-800 font-medium mb-3">Refunds may not be available for:</p>
              <ul className="list-disc list-inside space-y-2 text-red-700 text-sm">
                <li>Issues reported after the specified time limits</li>
                <li>Products consumed before reporting quality issues</li>
                <li>Cancellations after delivery has been dispatched</li>
                <li>Personal preference changes (taste, brand preferences)</li>
                <li>Issues caused by improper storage by customer</li>
                <li>Missed deliveries due to customer unavailability (without prior notice)</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Subscription Refunds</h2>
            <div className="text-gray-700 leading-relaxed space-y-3">
              <p><strong>Prepaid Subscriptions:</strong> Unused portions can be refunded upon request</p>
              <p><strong>Cancellation:</strong> 24-48 hours notice required for subscription cancellation</p>
              <p><strong>Partial Refunds:</strong> Available for unused days in monthly/weekly plans</p>
              <p><strong>Processing Time:</strong> Subscription refunds processed within 3-5 business days</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Customer Satisfaction Guarantee</h2>
            <div className="bg-green-50 rounded-lg p-6 border border-green-200">
              <p className="text-green-800 leading-relaxed">
                At Rightoz, your satisfaction is our priority. We are committed to resolving all issues 
                promptly and fairly. If you're not completely satisfied with our resolution, please 
                contact our customer support team for further assistance. We value your feedback and 
                continuously work to improve our products and services.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 mb-4">For any questions regarding refunds and returns:</p>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p><strong>Customer Support:</strong></p>
                  <p>📧 support@rightoz.com</p>
                  <p>📞 +91 91144 91158</p>
                  <p>💬 WhatsApp: +91 91144 91158</p>
                </div>
                <div>
                  <p><strong>Office Address:</strong></p>
                  <p>Plot No 48, Deep Vihar Dadi Ka Phatak</p>
                  <p>Benad Road Jhatwara</p>
                  <p>Jaipur, Rajasthan 302012</p>
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-4">
                Support Hours: 6:00 AM - 10:00 PM (7 days a week)
              </p>
            </div>
          </section>

        </div>

        {/* Back to Home */}
        <div className="text-center mt-12">
          <a
            href="/"
            className="inline-flex items-center bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
};

export default RefundReturns;
